import { Vehicle } from '../types/vehicle';
import { apiService } from './apiService';
import { VEHICLE_MAKES } from '../constants/vehicleConstants';

export const VEHICLE_SEPARATOR = '|';

// Define the structure for vehicle types
export interface VehicleType {
  id: string;
  name: string;
  years: string;
}

// Define the structure for vehicle models
export interface VehicleModel {
  id: string;
  name: string;
  types: VehicleType[];
}

// Define the structure for vehicle makes
export interface VehicleMake {
  id: string;
  name: string;
  models: VehicleModel[];
}

class VehicleService {
  private vehicleMakes: VehicleMake[] | null = null;
  private isLoading = false;
  private loadPromise: Promise<VehicleMake[]> | null = null;

  async getVehicleMakes(token?: string): Promise<VehicleMake[]> {
    if (this.vehicleMakes) {
      return this.vehicleMakes;
    }

    if (this.isLoading && this.loadPromise) {
      return this.loadPromise;
    }

    this.isLoading = true;
    this.loadPromise = this.fetchVehicleMakes(token);
    
    try {
      this.vehicleMakes = await this.loadPromise;
      return this.vehicleMakes;
    } finally {
      this.isLoading = false;
    }
  }

  private async fetchVehicleMakes(token?: string): Promise<VehicleMake[]> {
    try {
      const data = await apiService.getVehicles(token || null);
      
      if (!Array.isArray(data)) {
        console.error('API response is not an array, falling back to constants');
        return VEHICLE_MAKES;
      }
      
      console.log('Successfully fetched vehicles from API');
      return data as VehicleMake[];
    } catch (error) {
      console.warn('[vehicleService] Vehicle API failed; using fallback constants.', error);
      return VEHICLE_MAKES;
    }
  }

  async getVehicleMakeById(makeId: string, token?: string): Promise<VehicleMake | undefined> {
    const makes = await this.getVehicleMakes(token);
    return makes.find(make => make.id === makeId);
  }

  async getVehicleModelById(makeId: string, modelId: string, token?: string): Promise<VehicleModel | undefined> {
    const make = await this.getVehicleMakeById(makeId, token);
    return make?.models.find(model => model.id === modelId);
  }

  async getVehicleTypeById(makeId: string, modelId: string, typeId: string, token?: string): Promise<VehicleType | undefined> {
    const model = await this.getVehicleModelById(makeId, modelId, token);
    return model?.types.find(type => type.id === typeId);
  }

  async getAllVehicleMakes(token?: string): Promise<VehicleMake[]> {
    return this.getVehicleMakes(token);
  }

  async getVehicleModelsByMake(makeId: string, token?: string): Promise<VehicleModel[]> {
    const make = await this.getVehicleMakeById(makeId, token);
    return make?.models || [];
  }

  async getVehicleTypesByModel(makeId: string, modelId: string, token?: string): Promise<VehicleType[]> {
    const model = await this.getVehicleModelById(makeId, modelId, token);
    return model?.types || [];
  }

  async getVehicleNameById(id: string, token?: string): Promise<string> {
    const [makeId, modelId, typeId] = id.split(VEHICLE_SEPARATOR);
    const make = await this.getVehicleMakeById(makeId, token);
    const model = await this.getVehicleModelById(makeId, modelId, token);
    const type = await this.getVehicleTypeById(makeId, modelId, typeId, token);

    return `${make?.name || ''} ${model?.name || ''} ${type?.name || ''}`.trim();
  }

  async getVehicleById(id: string, token?: string): Promise<Vehicle> {
    const [makeId, modelId, typeId] = id.split(VEHICLE_SEPARATOR);
    const make = await this.getVehicleMakeById(makeId, token);
    const model = await this.getVehicleModelById(makeId, modelId, token);
    const type = await this.getVehicleTypeById(makeId, modelId, typeId, token);

    return {
      id,
      name: await this.getVehicleNameById(id, token),
      make: make?.id || '',
      model: model?.id || '',
      type: type?.id || ''
    };
  }

  // Clear cache when needed (e.g., on logout)
  clearCache(): void {
    this.vehicleMakes = null;
    this.loadPromise = null;
    this.isLoading = false;
  }
}

export const vehicleService = new VehicleService();